import React from 'react';
import { Group, GroupApi } from './GroupApi';
import { StatusContextValue, useContextStatus } from '../common/ContextStatus';
import { sortByString } from '../common/Sorting';
import { useOktaAuth } from '@okta/okta-react';

type GroupContextValue = StatusContextValue & {
  groups: Group[];
  setGroups: (groups: Group[]) => void;
  originalGroups: Group[];
  setOriginalGroups: (groups: Group[]) => void;
  hasMoreGroups: boolean;
  setHasMoreGroups: (hasMore: boolean) => void;
  totalGroups: number;
  setTotalGroups: (total: number) => void;
};

const GroupContext = React.createContext<GroupContextValue | undefined>(
  undefined
);

type GroupProviderProps = {
  children: React.ReactNode;
};

const GroupProvider = (props: GroupProviderProps) => {
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [originalGroups, setOriginalGroups] = React.useState<Group[]>([]);
  const [hasMoreGroups, setHasMoreGroups] = React.useState<boolean>(false);
  const [totalGroups, setTotalGroups] = React.useState<number>(0);

  const [contextStatus, setContextStatus] = React.useState({
    loading: false,
    saving: false,
    hasContent: false
  });

  const value: GroupContextValue = React.useMemo(
    () => ({ groups, setGroups, originalGroups, setOriginalGroups, hasMoreGroups, setHasMoreGroups, totalGroups, setTotalGroups, contextStatus, setContextStatus }),
    [groups, originalGroups, hasMoreGroups, totalGroups, contextStatus]
  );
  return <GroupContext.Provider value={value} {...props} />;
};

const useGroupContext = () => {
  const context = React.useContext(GroupContext);
  const { authState } = useOktaAuth();

  if (!context) {
    throw new Error('useGroupContext must be used within a GroupProvider');
  }

  const status = useContextStatus(context);
  const { groups, setGroups, originalGroups, setOriginalGroups, hasMoreGroups, setHasMoreGroups, totalGroups, setTotalGroups, contextStatus } = context;

  const load = (assetId: string | undefined): Promise<void> => {
    status.loadingStarted();

    if (assetId === undefined) {
      setGroups([]);
      setOriginalGroups([]);
      setHasMoreGroups(false);
      setTotalGroups(0);
      return Promise.resolve();
    }

    return GroupApi.getGroupsWithSelection(authState?.accessToken, assetId)
      .then(result => {
        sortByString(result.groups, group => group.name);
        setGroups(result.groups);
        setOriginalGroups(result.groups.map(g => ({ ...g })));
        setHasMoreGroups(result.hasMore);
        setTotalGroups(result.totalElements);
        status.succeed();
      })
      .catch(err => status.failed(err.message, 'Could not load groups'));
  };

  const save = (assetId: string): Promise<void> => {
    status.savingStarted();

    if (hasMoreGroups) {
      status.succeed();
      return Promise.resolve();
    }

    const originalSelectedIds = new Set(
      originalGroups.filter(g => g.selected).map(g => g.id)
    );
    const currentSelectedIds = new Set(
      groups.filter(g => g.selected).map(g => g.id)
    );

    const groupsToAdd = groups
      .filter(g => g.selected && !originalSelectedIds.has(g.id))
      .map(g => g.id);
    const groupsToRemove = originalGroups
      .filter(g => g.selected && !currentSelectedIds.has(g.id))
      .map(g => g.id);

    const promises: Promise<void>[] = [];

    if (groupsToAdd.length > 0) {
      promises.push(GroupApi.addAssetToGroups(authState?.accessToken, assetId, groupsToAdd));
    }
    if (groupsToRemove.length > 0) {
      promises.push(GroupApi.removeAssetFromGroups(authState?.accessToken, assetId, groupsToRemove));
    }

    if (promises.length === 0) {
      status.succeed();
      return Promise.resolve();
    }

    return Promise.all(promises)
      .then(() => {
        setOriginalGroups(groups.map(g => ({ ...g })));
        status.succeed('Groups was saved');
      })
      .catch(err => status.failed(err.message, 'Could not save groups'));
  };

  const onGroupSelected = (groupId: string, selected: boolean) =>
    setGroups(
      groups.map(group =>
        group.id === groupId ? { ...group, selected } : group
      )
    );

  const onGroupSelectedByName = (groupName: string, selected: boolean) =>
    setGroups(
      groups.map(group =>
        group.name === groupName ? { ...group, selected } : group
      )
    );

  return {
    groups,
    hasMoreGroups,
    totalGroups,
    load,
    save,
    onGroupSelected,
    onGroupSelectedByName,
    contextStatus,
    dismissError: status.dismissError,
    dismissSuccess: status.dismissSuccess
  };
};

export { GroupProvider, useGroupContext };
